import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

/* THUNKS & SELECTORS */
import { fetchTournaments } from "../../redux/admin/tournaments.thunk";
import { selectTournamentList } from "../../redux/admin/tournaments.selector";
import { fetchProfitLoss } from "../../redux/admin/profitLoss.thunk";
import {
  selectProfitLossData,
  selectProfitLossSummary,
} from "../../redux/admin/profitLoss.selector";

const ProfitLoss = () => {
  const dispatch = useDispatch();

  /* ================= STATE ================= */
  const tournaments = useSelector(selectTournamentList);
  const data = useSelector(selectProfitLossData);
  const summary = useSelector(selectProfitLossSummary);
  const loading = useSelector((state) => state.admin?.profitLoss?.loading);

  const [tournamentId, setTournamentId] = useState("");

  /* ================= EFFECTS ================= */
  useEffect(() => {
    dispatch(fetchTournaments());
  }, [dispatch]);

  const loadData = useCallback(() => {
    if (!tournamentId) return;
    dispatch(fetchProfitLoss({ tournament_id: tournamentId }));
  }, [dispatch, tournamentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ================= EXCEL EXPORT ================= */
  const handleExportExcel = () => {
    if (!data.length) return;

    let csv = "S.No,Type,Name,Credit,Debit\n";
    data.forEach((item, index) => {
      csv += `${index + 1},${item.type},${item.name},${item.income || 0},${item.expense || 0}\n`;
    });

    csv += `\n,,Total,${summary.income},${summary.expense}\n`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "profit-loss.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="precise-app-bg">
      <div className="precise-card">
        {/* HEADER */}
        <header className="precise-header">
          <div className="title-area">
            <h1>Profit & Loss</h1>
            <p>Tournament Financial Summary</p>
          </div>
          <button className="btn-download" onClick={handleExportExcel}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export Excel
          </button>
        </header>

        {/* TOOLBAR */}
        <section className="precise-toolbar">
          <div className="search-wrapper">
             {/* Empty or Search placeholder if needed */}
          </div>
          <div className="filter-wrapper">
            <span className="label-tiny">Tournament:</span>
            <select value={tournamentId} onChange={(e) => setTournamentId(e.target.value)}>
              <option value="">Select Event...</option>
              {tournaments.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </section>

        {/* SUMMARY SECTION - Using the styled summary card */}
        {tournamentId && (
          <div style={{ padding: "0 2rem" }}>
            <div className="payment-summary-card" style={{ marginTop: "1rem", marginBottom: "1rem" }}>
              <div className="summary-left">
                <span className="summary-label">Total Credit</span>
                <span className="summary-value" style={{ color: "#16a34a" }}>₹{summary.income}</span>
              </div>
              <div className="summary-left">
                <span className="summary-label">Total Debit</span>
                <span className="summary-value" style={{ color: "#dc2626" }}>₹{summary.expense}</span>
              </div>
              <div className="summary-left">
                <span className="summary-label">Net Profit</span>
                <span className="summary-value">₹{summary.profit}</span>
              </div>
            </div>
          </div>
        )}

        {/* TABLE */}
        <div className="table-responsive">
          <table className="precise-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Type</th>
                <th>Name</th>
                <th style={{ textAlign: "right" }}>Credit (In)</th>
                <th style={{ textAlign: "right" }}>Debit (Out)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="state-msg">Syncing database...</td></tr>
              ) : !tournamentId ? (
                <tr><td colSpan="5" className="state-msg">Select a tournament to load financial data</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="5" className="state-msg">No transactions found for this tournament</td></tr>
              ) : (
                data.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td><span className="cat-pill">{item.type}</span></td>
                    <td><span className="u-name">{item.name}</span></td>
                    <td style={{ textAlign: "right", color: item.income > 0 ? "#16a34a" : "inherit" }}>
                        {item.income > 0 ? `₹${item.income}` : "—"}
                    </td>
                    <td style={{ textAlign: "right", color: item.expense > 0 ? "#dc2626" : "inherit" }}>
                        {item.expense > 0 ? `₹${item.expense}` : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .precise-app-bg { background: #f8fafc; min-height: 100vh; padding: 2rem 1rem; font-family: 'Inter', system-ui, sans-serif; }
        .precise-card { max-width: 1000px; margin: 0 auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); overflow: hidden; }
        .precise-header { padding: 1.5rem 2rem; display: flex; justify-content: space-between; align-items: center; background: #fff; border-bottom: 1px solid #f1f5f9; }
        .precise-header h1 { font-size: 1.1rem; font-weight: 800; color: #0f172a; margin: 0; }
        .precise-header p { font-size: 12px; color: #64748b; margin: 2px 0 0; }
        .precise-toolbar { padding: 0.75rem 2rem; background: #fcfdfe; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
        .filter-wrapper { display: flex; align-items: center; gap: 8px; }
        .label-tiny { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; }
        .filter-wrapper select { border: 1px solid #e2e8f0; border-radius: 6px; padding: 6px 10px; font-size: 12px; background: #fff; }
        .precise-table { width: 100%; border-collapse: collapse; }
        .precise-table th { text-align: left; padding: 12px 2rem; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #f1f5f9; }
        .precise-table td { padding: 12px 2rem; font-size: 13px; border-bottom: 1px solid #f8fafc; color: #334155; }
        .u-name { display: block; font-weight: 600; color: #1e293b; }
        .cat-pill { background: #f1f5f9; padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 700; color: #475569; }
        .btn-download { display: inline-flex; align-items: center; gap: 8px; background: #ffffff; color: #16a34a; border: 1px solid #dcfce7; padding: 8px 16px; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .btn-download:hover { background: #f0fdf4; border-color: #16a34a; transform: translateY(-1px); }
        .payment-summary-card { padding: 18px 20px; border-radius: 12px; background: linear-gradient(135deg, #f8fafc, #f1f5f9); border: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; gap: 20px; flex-wrap: wrap; }
        .summary-left { display: flex; flex-direction: column; }
        .summary-label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; }
        .summary-value { font-size: 22px; font-weight: 800; color: #0f172a; }
        .state-msg { text-align: center; padding: 4rem; color: #94a3b8; font-size: 13px; font-style: italic; }
      `}</style>
    </div>
  );
};

export default ProfitLoss;